import moment from 'moment';
import { isLocalStorageSupported } from 'javascripts/utils';
import * as Sentry from '@sentry/browser';
import type { DraftValue } from './DraftManager';

export interface DraftData {
  value: DraftValue;
  savedAt: number;
}

export interface DraftStorageConfig {
  enclosingForm?: HTMLFormElement | null;
  keyPrefix?: string;
  keyValue?: string;
  isEmptyValue: (value: DraftValue) => boolean;
}

export default class DraftStorage {
  key: string;
  isEmptyValue: (value: DraftValue) => boolean;
  localStorageSupported: boolean;

  constructor({
    enclosingForm,
    keyPrefix,
    keyValue,
    isEmptyValue,
  }: DraftStorageConfig) {
    this.key = `${keyPrefix ?? ''}${keyValue ?? enclosingForm?.action ?? ''}`;
    this.localStorageSupported = isLocalStorageSupported();
    this.isEmptyValue = isEmptyValue;
  }

  get canStore(): boolean {
    return !!this.key && this.localStorageSupported;
  }

  get draft(): DraftValue | undefined {
    const { draftData } = this;
    return draftData && draftData.value;
  }

  get draftData(): DraftData | undefined {
    if (!this.canStore) return;

    const draftJSON = window.localStorage.getItem(this.key);
    let draftData;
    if (draftJSON) {
      draftData = JSON.parse(draftJSON);
    }

    if (!draftData) return;

    const draftExpired = moment().isAfter(
      moment(draftData.savedAt).add(3, 'days')
    );

    if (draftExpired) {
      this.deleteDraft();
      return;
    }
    return draftData;
  }

  saveDraft(value: DraftValue): void {
    if (!this.canStore) return;
    if (this.isEmptyValue(value)) {
      if (this.draft) {
        this.deleteDraft();
      }
      return;
    }

    try {
      window.localStorage.setItem(
        this.key,
        JSON.stringify({ value, savedAt: new Date().getTime() })
      );
    } catch (error) {
      if (error.code !== DOMException.QUOTA_EXCEEDED_ERR) {
        Sentry.captureException(error);
        console.error(error);
      }
    }
  }

  deleteDraft(): void {
    if (!this.canStore) return;
    window.localStorage.removeItem(this.key);
  }
}
